import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchOnboardingStatus } from '~/screens/job-apply/api/onboardingClient';
import { onboardingStage } from '~/screens/job-apply/lib/onboarding';
import { readPlan, type PlanState } from '~/screens/job-apply/lib/plan';

/**
 * ¿Esta persona terminó el alta? Es lo único que el shell del candidato
 * necesita saber, y decide dos cosas: qué barra se dibuja y si una ruta del
 * producto se puede abrir.
 *
 * ── POR QUÉ EL SHELL SE MEZCLA EN ESTO ─────────────────────────────────────
 *
 * Durante el alta la barra completa es una promesa falsa. "Mensajes" no tiene
 * mensajes, "Postulaciones" no tiene ninguna y "Panel" rebota de vuelta a
 * /onboarding en cuanto carga. Cuatro destinos que no llevan a nada le piden a
 * alguien que está contestando un formulario que evalúe si se está perdiendo
 * algo. Mientras el alta no esté hecha, el header es el logo y "Salir": una
 * sola cosa que hacer, que es la que está haciendo.
 *
 * ── LA CONDICIÓN ES CANAL + HOJA DE VIDA (+ SITUACIÓN MIGRATORIA) ──────────
 *
 * Desde el 2026-10-11, quien postula a EE. UU. contesta además «Tu situación
 * migratoria» (etapa 'immigration'): quien ya tenía el alta hecha vuelve UNA
 * vez a /onboarding a contestarla. En «Ver como» no se exige (ver
 * `needsImmigrationStep`).
 *
 * `onboardingStage` y no `isOnboardingComplete`: la segunda además exige que la
 * membresía esté ACTIVE, y una cuenta suspendida por pago SÍ tiene que poder
 * navegar —su panel es justamente donde está el camino para volver—. Suspender
 * a alguien no puede devolverlo al formulario de alta.
 *
 * ── EL PARPADEO ────────────────────────────────────────────────────────────
 *
 * Dibujar la barra completa y quitarla medio segundo después es peor que
 * tardar en dibujarla: lo primero se lee como un error de la app, lo segundo
 * como una pantalla que está cargando. Así que mientras no se sabe, la barra es
 * la mínima. Para que eso no le cueste un parpadeo en CADA recarga a quien ya
 * terminó, la respuesta "ya está" se recuerda en la sesión de la pestaña y sirve
 * de primer trazo; la petición que sale igual la confirma o la corrige.
 *
 * ── UN FALLO DE RED NO ES UN ALTA A MEDIAS ─────────────────────────────────
 *
 * Si la petición se cae, `known` queda en false: la barra se esconde (nunca se
 * promete lo que no se sabe) pero NADIE es sacado de su pantalla. Redirigir por
 * un timeout mandaría a rehacer el alta a quien ya la tiene.
 */

/**
 * Lo que el shell del candidato puede abrir con el alta a medias. `/socios`
 * también: el Programa de socios es para quien RECOMIENDA, y un líder de
 * comunidad que nunca va a postular no tiene por qué subir una hoja de vida
 * para poder pedir su código.
 */
const ONBOARDING_PATHS = ['/onboarding', '/connect', '/socios'];

/** Match por segmento completo: `/onboarding/screening` sí, `/onboarding-faq` no. */
export function isOnboardingPath(pathname: string): boolean {
  return ONBOARDING_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export interface OnboardingGate {
  /** Ya hay algo que dibujar: respuesta del servidor o el recuerdo de la pestaña. */
  ready: boolean;
  /** Canal de envío + hoja de vida. Lo que habilita la barra completa. */
  complete: boolean;
  /**
   * Hay una respuesta FRESCA del servidor. Lo único que autoriza a redirigir.
   *
   * En false por un fallo de red, y también mientras una revalidación está en
   * el aire: la persona que acaba de guardar su hoja de vida llega al panel con
   * la respuesta vieja todavía en memoria —la que decía "te falta"— y redirigir
   * con eso la devuelve al formulario que acaba de terminar.
   */
  known: boolean;
  /**
   * El plan, de la misma respuesta (onboarding-status ya lo trae). Null si el
   * servidor no lo dijo: sin su palabra no se pinta la cinta de «no tienes plan».
   */
  plan: PlanState | null;
  /**
   * `free_tier` de onboarding-status: postular a mano sin plan está prendido.
   * Ausente = false: sin la palabra del servidor todo queda como antes.
   */
  freeTier?: boolean;
  /**
   * Una cuenta que ya existía debe ver `ConsentStep` una vez: `needs_consent`
   * del servidor con `free_tier` prendido, y que no la hayan cerrado en esta
   * sesión. Cerrarla (`dismissConsent`) no escribe nada: la cuenta sigue privada.
   */
  needsConsent: boolean;
  dismissConsent: () => void;
  /** La hoja pública (`public_profile_url`): solo si es pública y existe de verdad. */
  publicProfileUrl: string | null;
}

interface Entry {
  complete: boolean;
  known: boolean;
  plan: PlanState | null;
  freeTier?: boolean;
  needsConsent?: boolean;
  publicProfileUrl?: string | null;
  at: number;
}

const REMEMBER_KEY = 'tbit.jobapply.onboarded';
const CONSENT_DISMISSED_KEY = 'tbit.jobapply.consentDismissed';
const TTL_MS = 30_000;

function consentDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(CONSENT_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

let cache: Entry | null = null;
let inflight: Promise<Entry> | null = null;

/** Sólo para las pruebas: una caché de módulo no se puede reusar sin esto. */
export function resetOnboardingGate(): void {
  cache = null;
  inflight = null;
  try {
    window.sessionStorage.removeItem(REMEMBER_KEY);
    window.sessionStorage.removeItem(CONSENT_DISMISSED_KEY);
  } catch {
    // Sin storage no hay nada que resetear.
  }
}

/** La próxima lectura vuelve a preguntar. */
export function invalidateOnboardingGate(): void {
  cache = null;
}

function remember(complete: boolean): void {
  try {
    if (complete) window.sessionStorage.setItem(REMEMBER_KEY, '1');
    else window.sessionStorage.removeItem(REMEMBER_KEY);
  } catch {
    // Modo privado: el recuerdo es una conveniencia, no un requisito.
  }
}

/**
 * El primer trazo de una pestaña que ya sabía la respuesta. `at: 0` a propósito:
 * nace vencido, así que sirve para pintar pero no evita la petición.
 */
function seed(): Entry | null {
  try {
    return window.sessionStorage.getItem(REMEMBER_KEY) === '1'
      ? { complete: true, known: true, plan: null, at: 0 }
      : null;
  } catch {
    return null;
  }
}

function fresh(): boolean {
  return !!cache && Date.now() - cache.at < TTL_MS;
}

async function resolve(): Promise<Entry> {
  if (fresh()) return cache as Entry;
  if (inflight) return inflight;

  inflight = (async () => {
    let entry: Entry;
    try {
      const status = await fetchOnboardingStatus();
      entry = {
        complete: onboardingStage(status) === 'complete',
        known: true,
        plan: typeof (status as { plan_active?: unknown }).plan_active === 'boolean' ? readPlan(status) : null,
        freeTier: status.free_tier === true,
        needsConsent: status.free_tier === true && status.needs_consent === true,
        publicProfileUrl: status.public_profile_url ?? null,
        at: Date.now(),
      };
      remember(entry.complete);
    } catch {
      entry = { complete: false, known: false, plan: null, at: Date.now() };
    }
    cache = entry;
    inflight = null;
    return entry;
  })();

  return inflight;
}

export function useOnboardingGate(): OnboardingGate {
  const { pathname } = useLocation();
  const [entry, setEntry] = useState<Entry | null>(() => (fresh() ? cache : seed()));
  const [pending, setPending] = useState(!fresh());
  const [dismissed, setDismissed] = useState(consentDismissed);

  useEffect(() => {
    let cancelled = false;

    // Con el alta a medias se revalida en CADA navegación: la persona acaba de
    // guardar su hoja de vida en la pantalla anterior, y la barra tiene que
    // aparecer al llegar al panel, no cuando venza una caché. Al terminar el
    // alta la caché vuelve a mandar y esto deja de costar una petición.
    if (cache && !cache.complete) invalidateOnboardingGate();
    if (!fresh()) setPending(true);

    const done = (next: Entry) => {
      if (cancelled) return;
      setEntry(next);
      setPending(false);
    };

    resolve()
      .then(done)
      // resolve() ya absorbe sus fallos; llegar acá es un imposible, y la
      // respuesta correcta sigue siendo "no sabemos".
      .catch(() => done({ complete: false, known: false, plan: null, at: Date.now() }));

    return () => {
      cancelled = true;
    };
  }, [pathname]);

  return {
    ready: entry !== null,
    // La respuesta vieja se sigue DIBUJANDO mientras llega la nueva: cambiar de
    // barra en cada navegación parpadea, y casi siempre la nueva dice lo mismo.
    complete: entry?.complete ?? false,
    known: !pending && (entry?.known ?? false),
    plan: entry?.plan ?? null,
    freeTier: entry?.freeTier === true,
    needsConsent: entry?.needsConsent === true && !dismissed,
    publicProfileUrl: entry?.publicProfileUrl ?? null,
    dismissConsent: () => {
      try {
        window.sessionStorage.setItem(CONSENT_DISMISSED_KEY, '1');
      } catch {
        // Sin storage se cierra igual hasta recargar.
      }
      setDismissed(true);
    },
  };
}
